import { useState } from 'react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'

import { computeRewardEligibility, sortEligibleRewards } from '../model/rewardEligibility'

import type { QuestEligibleReward } from '../model/types'

type QuestRewardPickerProps = {
  rewards: QuestEligibleReward[]
  runScore: number
  busy?: boolean
  onRedeem: (rewardId: number) => void
}

const REASON_KEY = {
  sold_out: 'quests.reward.soldOut',
  inactive: 'quests.reward.inactive',
  insufficient_points: 'quests.reward.insufficientPoints'
} as const

export function QuestRewardPicker({ rewards, runScore, busy, onRedeem }: QuestRewardPickerProps) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<QuestEligibleReward | null>(null)
  const sorted = sortEligibleRewards(rewards)

  const selectedEligibility = selected ? computeRewardEligibility(selected, runScore) : null

  return (
    <div className="flex w-full max-w-sm flex-col gap-3">
      <p className="m-0 text-sm font-bold text-gray-900">{t('quests.reward.title')}</p>
      <p className="m-0 text-xs text-gray-500">{t('quests.reward.subtitle')}</p>
      <p className="m-0 text-xs text-gray-500">
        {t('quests.reward.yourScore')}: <strong>{runScore}</strong>
      </p>

      <div className="grid grid-cols-2 gap-2">
        {sorted.map(reward => {
          const eligibility = computeRewardEligibility(reward, runScore)
          const imageUrl = reward.image?.file_url ?? reward.image?.file?.url

          return (
            <Card
              key={reward.id}
              className="cursor-pointer p-3"
              onClick={() => setSelected(reward)}
            >
              {imageUrl && (
                <img src={imageUrl} alt="" className="mb-2 h-16 w-full rounded-lg object-cover" />
              )}
              <p className="m-0 text-xs font-bold text-gray-900">{reward.name}</p>
              <p className="m-0 text-2xs text-gray-500">
                {reward.redeem_points_required} pts
              </p>
              {!eligibility.eligible && eligibility.reason && (
                <p className="m-0 text-2xs font-bold text-red-600">
                  {t(REASON_KEY[eligibility.reason])}
                </p>
              )}
            </Card>
          )
        })}
      </div>

      <Dialog open={!!selected} onOpenChange={open => !open && setSelected(null)}>
        <DialogContent>
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.name}</DialogTitle>
                <DialogDescription>
                  {t('quests.reward.minimumRequired')}: {selected.redeem_points_required} pts
                </DialogDescription>
              </DialogHeader>

              {selectedEligibility && !selectedEligibility.eligible && selectedEligibility.reason && (
                <p className="text-sm font-bold text-red-600">
                  {t(REASON_KEY[selectedEligibility.reason])}
                </p>
              )}

              <Button
                variant="primary"
                size="lg"
                loading={busy}
                disabled={!selectedEligibility?.eligible}
                onClick={() => onRedeem(selected.id)}
              >
                {t('quests.reward.redeem')}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
